import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AuthClient } from '@/contexts/auth';
import type { OverviewLocation } from '@/domain/client';
import { createCagePageUrl } from '@/utils/urlGenerator';

import type { BreadcrumbCage, BreadcrumbLocation } from './types';

interface BreadcrumbNavigationInput {
    client?: AuthClient;
    currentLocation: OverviewLocation | null;
    setCurrentLocation: Dispatch<SetStateAction<OverviewLocation | null>>;
    pagePath: string;
    setClientNameFilter: Dispatch<SetStateAction<string>>;
}

export interface BreadcrumbNavigation {
    handleHomeNavigation: () => void;
    handleClientNavigation: (client: AuthClient) => void;
    handleLocationNavigation: (location: BreadcrumbLocation) => void;
    handleCageNavigation: (cage: BreadcrumbCage) => void;
}

/** Where picking an entry at each level of the trail takes the reader. */
export function useBreadcrumbNavigation(input: BreadcrumbNavigationInput): BreadcrumbNavigation {
    const { client, currentLocation, setCurrentLocation, pagePath, setClientNameFilter } = input;
    const navigate = useNavigate();

    const handleHomeNavigation = useCallback(() => {
        if (!client) return;
        navigate(`/c/${client.id}`);
        setCurrentLocation(null);
    }, [client, navigate, setCurrentLocation]);

    const handleClientNavigation = (client: AuthClient) => {
        setClientNameFilter('');
        navigate(`/c/${client.id}/`);
    };

    const handleLocationNavigation = useCallback(
        (location: BreadcrumbLocation) => {
            if (client) {
                navigate(`/c/${client.id}/location/${location.id}`);
            }
        },
        [client, navigate]
    );

    const handleCageNavigation = useCallback(
        (cage: BreadcrumbCage) => {
            if (!currentLocation || !client) return;

            // Each entry carries its *own* pen's cohort, never the one being left
            // behind: an `fg` from another pen is meaningless there, and the
            // backend would ignore it and quietly fall back.
            navigate(
                createCagePageUrl(
                    client.id,
                    currentLocation.locationId,
                    cage.id,
                    pagePath || '/dashboard',
                    cage.fishGroupId
                )
            );
        },
        [client, currentLocation, pagePath, navigate]
    );

    return {
        handleHomeNavigation,
        handleClientNavigation,
        handleLocationNavigation,
        handleCageNavigation,
    };
}
